import React, { useState } from 'react';
import BEM from '../../bem';
import tzTime from '../../utils/tzTime';
import { ReactComponent as CheckMarkIcon } from '../../../common/images/checkmark.svg';
import type { Task, TaskPriority } from 'js-sdk/src/types/Tasks';
import { useCompleteTaskMutation, useReopenTaskMutation } from 'redux-stores/tasks';

const classes = BEM.with('TaskCard');

const PRIORITY_LABEL: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

type TaskCardProps = {
  task: Task;
  organizationId: string;
};

export default function TaskCard({ task, organizationId }: TaskCardProps) {
  const [completeTask, { isLoading: isCompleting }] = useCompleteTaskMutation();
  const [reopenTask, { isLoading: isReopening }] = useReopenTaskMutation();
  const [toggleError, setToggleError] = useState<string | null>(null);

  const isCompleted = task.status === 'completed';
  const isBusy = isCompleting || isReopening;
  const isOverdue = !isCompleted && task.due_at ? new Date(task.due_at) < new Date() : false;
  const dueLabel = task.due_at ? tzTime(task.due_at).format('MMM D, h:mm A') : null;

  const toggleComplete = async () => {
    if (isBusy) return;
    setToggleError(null);
    try {
      if (isCompleted) {
        await reopenTask({ organizationId, taskId: task.id }).unwrap();
      } else {
        await completeTask({ organizationId, taskId: task.id }).unwrap();
      }
    } catch {
      setToggleError(isCompleted ? 'Could not reopen task.' : 'Could not complete task.');
    }
  };

  return (
    <div className={classes('', { completed: isCompleted })} data-test-id="task-card">
      <div className={classes('top')}>
        {task.priority && (
          <span className={classes('priority-pill', { priority: task.priority })}>
            {PRIORITY_LABEL[task.priority]}
          </span>
        )}
      </div>
      <div className={classes('body')}>
        <div>
          <button
            aria-checked={isCompleted}
            aria-label={isCompleted ? 'Reopen task' : 'Complete task'}
            className={classes('checkbox', { checked: isCompleted })}
            data-test-id="task-card-checkbox"
            disabled={isBusy}
            onClick={toggleComplete}
            role="checkbox"
            type="button"
          >
            {isCompleted && <CheckMarkIcon aria-hidden className={classes('checkmark')} />}
          </button>
        </div>
        <div>
          <div className={classes('title', { completed: isCompleted })}>{task.title}</div>
          {task.description && <div className={classes('description')}>{task.description}</div>}
        </div>
      </div>
      <div className={classes('bottom')}>
        {dueLabel && (
          <span className={classes('due-pill', { overdue: isOverdue })}>
            {isOverdue ? 'Overdue' : dueLabel}
          </span>
        )}
      </div>
      {toggleError && (
        <div className={classes('toggle-error')} data-test-id="task-card-error" role="alert">
          {toggleError}
        </div>
      )}
    </div>
  );
}
